import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";

const STATUSES = ["New", "Pending", "Confirmed", "Completed", "Cancelled"];
const STATUS_COLOR = {
  New: "text-ember border-ember", Pending: "text-yellow-400 border-yellow-400/60",
  Confirmed: "text-blue-400 border-blue-400/60", Completed: "text-green-400 border-green-400/60",
  Cancelled: "text-[#828290] border-ash",
};

export default function AdminBookings() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [artist, setArtist] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const { data: artists = [] } = useQuery({ queryKey: ["admin-artists"], queryFn: async () => (await api.get("/admin/artists")).data });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["bookings", status, artist, search],
    queryFn: async () => (await api.get("/admin/bookings", { params: { status: status || undefined, artist: artist || undefined, search: search || undefined } })).data,
  });

  const artistMap = Object.fromEntries(artists.map((a) => [a.slug, a.name]));
  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
      setDetail((d) => (d && d.id === id ? { ...d, status: newStatus } : d));
    } catch { toast.error("Update failed"); }
  };

  const counts = useMemo(() => bookings.length, [bookings]);

  return (
    <AdminLayout title="Bookings">
      {/* filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828290]" />
          <input data-testid="booking-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…"
            className="w-full bg-charcoal border border-ash text-white pl-9 pr-4 py-2.5 text-sm outline-none focus:border-ember" />
        </div>
        <select data-testid="booking-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-charcoal border border-ash text-white px-4 py-2.5 text-sm outline-none focus:border-ember">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="booking-filter-artist" value={artist} onChange={(e) => setArtist(e.target.value)} className="bg-charcoal border border-ash text-white px-4 py-2.5 text-sm outline-none focus:border-ember">
          <option value="">All artists</option>
          {artists.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
        </select>
      </div>

      <p className="font-mono text-xs uppercase tracking-widest text-[#828290] mb-4" data-testid="booking-count">{counts} bookings</p>

      {isLoading ? <p className="text-[#828290] font-mono text-sm">Loading…</p> : bookings.length === 0 ? (
        <div className="border border-ash p-16 text-center text-[#828290]">No bookings match.</div>
      ) : (
        <div className="border border-ash overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ash text-left font-mono text-[0.6rem] uppercase tracking-widest text-[#828290]">
                <th className="p-4">Client</th><th className="p-4 hidden md:table-cell">Style</th>
                <th className="p-4 hidden md:table-cell">Artist</th><th className="p-4 hidden lg:table-cell">Date</th>
                <th className="p-4">Status</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-ash hover:bg-charcoal transition-colors">
                  <td className="p-4"><div className="text-white">{b.name}</div><div className="text-[#828290] text-xs">{b.email}</div></td>
                  <td className="p-4 hidden md:table-cell text-[#C4C4CC]">{styleMap[b.style_slug] || "—"}</td>
                  <td className="p-4 hidden md:table-cell text-[#C4C4CC]">{b.recommend_artist ? "Recommend" : (artistMap[b.artist_slug] || "—")}</td>
                  <td className="p-4 hidden lg:table-cell text-[#828290] font-mono text-xs">{b.preferred_date || "—"}</td>
                  <td className="p-4"><span className={`inline-block font-mono text-[0.6rem] uppercase tracking-widest border px-2 py-1 ${STATUS_COLOR[b.status]}`}>{b.status}</span></td>
                  <td className="p-4 text-right"><button data-testid={`booking-view-${b.id}`} onClick={() => setDetail(b)} className="font-mono text-[0.65rem] uppercase tracking-widest text-ember hover:text-white">View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-[80] flex justify-end" data-testid="booking-detail">
          <div className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-lg bg-charcoal border-l border-ash h-full overflow-y-auto p-8">
            <div className="flex items-start justify-between mb-8">
              <div><p className="eyebrow mb-2">Booking #{detail.id.slice(0, 8)}</p><h2 className="font-serif text-4xl text-white">{detail.name}</h2></div>
              <button onClick={() => setDetail(null)} className="text-[#828290] hover:text-white"><X size={22} /></button>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`booking-set-${s.toLowerCase()}`} onClick={() => updateStatus(detail.id, s)}
                  className={`font-mono text-[0.6rem] uppercase tracking-widest px-3 py-2 border transition-colors ${detail.status === s ? "bg-ember text-obsidian border-ember" : "border-ash text-[#C4C4CC] hover:border-white"}`}>{s}</button>
              ))}
            </div>

            <dl className="space-y-4 text-sm">
              <D k="Email" v={detail.email} />
              <D k="Phone" v={detail.phone} />
              <D k="Style" v={styleMap[detail.style_slug] || "—"} />
              <D k="Artist" v={detail.recommend_artist ? "Recommend for me" : (artistMap[detail.artist_slug] || "—")} />
              <D k="Project" v={detail.project_type || "—"} />
              <D k="Placement" v={detail.placement || "—"} />
              <D k="Size" v={detail.size || "—"} />
              <D k="Preferred" v={`${detail.preferred_date || "—"} ${detail.preferred_time || ""}`} />
              <D k="Created" v={new Date(detail.created_at).toLocaleString()} />
            </dl>

            <div className="mt-6">
              <p className="eyebrow mb-2">Description</p>
              <p className="text-[#C4C4CC] leading-relaxed">{detail.description}</p>
            </div>

            {detail.reference_images?.length > 0 && (
              <div className="mt-6">
                <p className="eyebrow mb-3">References</p>
                <div className="grid grid-cols-3 gap-2">
                  {detail.reference_images.map((p) => (
                    <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square border border-ash overflow-hidden"><img src={fileUrl(p)} alt="ref" className="w-full h-full object-cover" /></a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function D({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-ash pb-3">
      <dt className="font-mono text-[0.6rem] uppercase tracking-widest text-[#828290] shrink-0">{k}</dt>
      <dd className="text-white text-right break-all">{v}</dd>
    </div>
  );
}
